import { RCAlertBox, RCButton, RCLoadingBars, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import React from 'react';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { flowRight } from 'lodash';
import { FetchResult, MutationFn } from 'react-apollo';
import {
  RequestTaxFormDownload,
  FetchTaxFormDownload,
  core_apimessages_TaxFormDownload_Status as TaxFormDownloadStatus,

} from '../../../schemas/rql';
import DownloadIcon from '@reverbdotcom/cadence/icons/core/download.svg?component';
import LockIcon from '@reverbdotcom/cadence/icons/core/lock.svg?component';
import { NoTaxFormsToDisplay } from './no_tax_forms_to_display';
import experiments from '@reverbdotcom/commons/src/experiments';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { IUserContext, withUserContext } from '@reverbdotcom/commons';
import { TaxProfileStatus } from '../tax_profile_status';
import FormSection from '@reverbdotcom/commons/src/components/form_section';

export interface ITaxForm {
  id: string;
  taxFormType: string;
  taxYear: number;
}


interface ExternalProps {
  taxForm: ITaxForm;
  taxFormDownload: FetchTaxFormDownload._TaxFormDownload;
  lastRequestedAt: string;
}

interface MutationProps {
  requestTaxFormDownload: MutationFn<RequestTaxFormDownload.Mutation, RequestTaxFormDownload.Variables>;
  fetchTaxFormDownload: MutationFn<FetchTaxFormDownload.Mutation, FetchTaxFormDownload.Variables>;
}

type Props = ExternalProps & MutationProps & IUserContext;


export function TaxForms({ taxForm, taxFormDownload, lastRequestedAt, user, requestTaxFormDownload, fetchTaxFormDownload }: Props) {
  const [loading, setLoading] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [downloadedTaxForm, setDownloadedTaxForm] = React.useState(taxFormDownload);

  React.useEffect(() => {
    if (downloadedTaxForm && downloadedTaxForm.status === TaxFormDownloadStatus.PENDING) {
      requestForm(`${downloadedTaxForm.id}`);
    }
  }, []);

  if (!isExperimentEnabled(user, experiments.THOMSON_REUTERS_FORMS)) {
    return null;
  }


  const requestForm = async (taxFormDownloadId: string = null) => {
    setLoading(true);
    setShowError(false);
    let id = taxFormDownloadId;
    try {
      if (!id) {
        const response = await makeFormRequest();
        ({ id } = response.data.requestTaxFormDownload.taxFormDownload);
      }
      const downloadFormData: FetchResult<FetchTaxFormDownload.Mutation, Record<string, any>> = await pollForFormDownload(id);
      const { status } = downloadFormData.data.taxFormDownload.taxFormDownload;

      if (status === TaxFormDownloadStatus.COMPLETE) {
        setDownloadedTaxForm(downloadFormData.data.taxFormDownload.taxFormDownload);
      } else {
        setShowError(true);
      }
    } catch {
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const makeFormRequest = async () => {
    const response = await requestTaxFormDownload({ variables: {
      input: {
        taxFormId: `${taxForm.id}`,
      },
    } });
    if (!response || response.data?.requestTaxFormDownload.error) {
      throw new Error('error');
    }
    return response;
  };


  const pollForFormDownload = async (taxFormDownloadId: string) => {
    let downloadFormData: void | FetchResult<FetchTaxFormDownload.Mutation, Record<string, any>>;
    let status: TaxFormDownloadStatus;
    do {
      await new Promise(r => setTimeout(r, 5000));

      downloadFormData = await fetchTaxFormDownload({ variables: { input: { id: taxFormDownloadId } } });
      if (!downloadFormData) {
        throw new Error('error');
      }
      ({ status } = downloadFormData.data.taxFormDownload.taxFormDownload);
    }
    while (status === TaxFormDownloadStatus.PENDING);
    return downloadFormData;
  };

  const shouldShowComingSoon = () => {
    return !taxForm;
  };

  const shouldShowRequestDownload = () => {
    return taxForm &&
    !loading &&
    !shouldShowDownloadButton();
  };

  const shouldShowDownloadButton = () => {
    return downloadedTaxForm && downloadedTaxForm.status === TaxFormDownloadStatus.COMPLETE;
  };

  const shouldDisplayFormDownloadSection = () => {
    return showError || loading || shouldShowDownloadButton();
  };

  const shouldDisplayThankYouSection = () => {
    return loading || shouldShowDownloadButton();
  };

  return (
    <FormSection
      title={
        <>
          <div className='mtb-1'>
            <TaxProfileStatus
              isComplete={!!lastRequestedAt}
              message={lastRequestedAt ?
                I18n.t('discovery.taxProfile.taxForms.downloadRequestedStatus', { date: lastRequestedAt })
                : I18n.t('discovery.taxProfile.taxForms.downloadIncompleteStatus')}
            />
          </div>
          <Translate
            id="tax-forms"
            text="discovery.taxProfile.taxForms.title"
          />
        </>
      }
      description={
        <Translate
          tag='p'
          text='discovery.taxProfile.taxForms.description'
        >
          <a href='mailto:1099taxreporting@reverb.com' className='text-link'>
            <Translate text='discovery.taxProfile.taxForms.emailLinkText' />
          </a>
        </Translate>
      }
    >
      { shouldShowComingSoon() && (
        <NoTaxFormsToDisplay/>
      )}
      { shouldShowRequestDownload() && (
        <div className='mt-2'>
          <RCButton
            variant='primary'
            onClick={() => requestForm()}
          >
            <Translate text="discovery.taxProfile.taxForms.requestDownload">
              { taxForm.taxYear }
            </Translate>
          </RCButton>
        </div>
      )}
      { shouldDisplayThankYouSection() && (
        <RCAlertBox
          type='success'
        >
          <Translate
            text="discovery.taxProfile.taxForms.requested"
          />
        </RCAlertBox>
      )}
      { shouldDisplayFormDownloadSection() && (
        <div className='bd-1 bd-color-primary padding-4 mt-2 d-flex fx-align-center fx-justify-center'>
          { showError && (
            <RCAlertBox
              type="error"
            >
              <Translate
                text="discovery.taxProfile.taxForms.failure"
              />
            </RCAlertBox>
          )}
          { loading && (
            <div className='d-flex fx-dir-col fx-align-center'>
              <div>
                <RCLoadingBars />
              </div>
              <div>
                <Translate
                  text="discovery.taxProfile.taxForms.loading"
                />
              </div>
            </div>
          )}
          { shouldShowDownloadButton() && (
            <div className='d-flex fx-dir-col fx-align-center'>
              <div>
                <a
                  href={downloadedTaxForm.url}
                  className="rc-button rc-button--primary"
                  target="_blank" rel="noreferrer"
                >
                  <RCTextWithIcon
                    placement="left"
                    svgComponent={DownloadIcon}
                  >
                    <Translate
                      text="discovery.taxProfile.taxForms.downloadTaxForm"
                    />
                  </RCTextWithIcon>
                </a>
              </div>
              <div className='mt-2 color-secondary'>
                <RCTextWithIcon
                  placement="left"
                  svgComponent={LockIcon}
                >
                  <Translate
                    text="discovery.taxProfile.taxForms.secureDownload"
                  />
                </RCTextWithIcon>
              </div>
            </div>
          )}
        </div>
      )}
    </FormSection>
  );
}

const REQUEST_TAX_FORM_DOWNLOAD = gql`
  mutation RequestTaxFormDownload(
    $input: Input_core_apimessages_RequestTaxFormDownloadRequest
  ) {
    requestTaxFormDownload(input: $input) {
      taxFormDownload {
        id
        status
      }
      error
    }
  }
`;

const FETCH_TAX_FORM_DOWNLOAD = gql`
  mutation FetchTaxFormDownload(
    $input: Input_core_apimessages_TaxFormDownloadRequest
  ) {
    taxFormDownload(input: $input) {
      taxFormDownload {
        id
        status
        url
      }
    }
  }
`;

const withRequestTaxFormDownload = withGraphql<ExternalProps, RequestTaxFormDownload.Mutation, RequestTaxFormDownload.Variables>(
  REQUEST_TAX_FORM_DOWNLOAD, {
    name: 'requestTaxFormDownload',
    options: {
      errorPolicy: 'all',
    },
  });

const withTaxFormDownload = withGraphql<ExternalProps, FetchTaxFormDownload.Mutation, FetchTaxFormDownload.Variables>(FETCH_TAX_FORM_DOWNLOAD, {
  name: 'fetchTaxFormDownload',
  options: {
    errorPolicy: 'all',
  },
});

export default flowRight([withUserContext, withRequestTaxFormDownload, withTaxFormDownload])(TaxForms);
